import { createContext, useContext, useState, useEffect } from "react";
import { useLoaderData } from "react-router-dom";
import { getUserByUsername } from "../services";
import { isStripeConnected } from "../services/payment";
import {
  isProfileComplete,
  calculateProfileCompletion,
  getMissingFields
} from "../utils";

// Create the context
const UserContext = createContext(null);

/**
 * UserProvider - Manages user data and profile completion state
 *
 * Features:
 * - Fetches fresh user data from API on mount
 * - Provides profile completion checks
 * - Offers refresh and update methods
 * - Handles loading and error states
 *
 * @param {React.ReactNode} children - Child components
 */
export const UserProvider = ({ children }) => {
  const user = useLoaderData(); // Get initial data from router loader (contains token)

  // State management
  const [userData, setUserData] = useState(null);
  const [isStripeAccountVerified, setIsStripeAccountVerified] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetches fresh user data from the API
   * @private
   */
  const fetchUserData = async () => {
    if (!user?.data?.userName || !user?.token) {
      setLoadingUserData(false);
      return;
    }

    setLoadingUserData(true);
    setError(null);

    try {
      const res = await getUserByUsername(user.data.userName, user.token);

      if (res?.status === true && res?.data) {
        setUserData(res.data);
        setError(null);

        // Fetch Stripe account is verified
        await fetchStripeAccountIsVerified();
      } else {
        // Fallback to loader data if API fails
        console.error("Failed to fetch fresh user data");
        setError(res?.message || "Failed to fetch user data");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError(err.message || "An error occurred while fetching user data");
    } finally {
      setLoadingUserData(false);
    }
  };

   /**
   * Fetches fresh user data from the API
   * @private
   */
  const fetchStripeAccountIsVerified = async () => {
    const res = await isStripeConnected(user?.token);

    if(res?.isVerified === true) {
      setIsStripeAccountVerified(true);
    } else {
      setIsStripeAccountVerified(false);
    }
  }

  /**
   * Manually refresh user data from API
   * Use this after profile updates or when you need fresh data
   *
   * @public
   * @returns {Promise<void>}
   */
  const refreshUserData = async () => {
    await fetchUserData();
  };

   /**
   * Manually refresh stripe account verified status from API
   * Use this after the stripe has been connected to the account
   *
   * @public
   * @returns {Promise<void>}
   */
  const refreshStripeAccountIsVerified = async () => {
    await fetchStripeAccountIsVerified();
  };

  /**
   * Update user data locally without making an API call
   * Use this when you already have fresh data from another API call
   *
   * @public
   * @param {Object} newData - Updated user data
   */
  const updateUserData = (newData) => {
    if (newData && typeof newData === "object") {
      setUserData(newData);
      setError(null);
    }
  };

  /**
   * Clear the error state
   *
   * @public
   */
  const clearError = () => {
    setError(null);
  };

  // Fetch user data on mount
  useEffect(() => {
    fetchUserData();
  }, [user?.data?.userName, user?.token]);

  // Computed values based on userData
  const profileComplete = isProfileComplete(userData);
  const completionPercent = calculateProfileCompletion(userData);
  const missingFields = !profileComplete ? getMissingFields(userData) : [];

  // Context value object
  const contextValue = {
    // User data
    userData,              // Fresh user data from API
    isStripeAccountVerified,// Boolean: is Stripe account verified
    loadingUserData,       // Loading state
    error,                 // Error message if any
    token: user?.token,    // Auth token from loader

    // Profile completion
    profileComplete,       // Boolean: is profile complete?
    completionPercent,     // Number: 0-100 completion percentage
    missingFields,         // Array: list of missing field names

    // Methods
    refreshUserData,       // Function: refresh data from API
    refreshStripeAccountIsVerified, // Function: refresh Stripe account is verified
    updateUserData,        // Function: update data locally
    clearError,            // Function: clear error
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Custom hook to access UserContext
 *
 * Usage:
 * ```jsx
 * const { userData, profileComplete, refreshUserData } = useUser();
 * ```
 *
 * @throws {Error} If used outside of UserProvider
 * @returns {Object} User context value
 */
export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};

// Named export for the context itself (if needed for advanced use cases)
export { UserContext };
